import React from "react";
import urlParse from "url-parse";
import { RoomInitializationError } from "../../errors";
import { useRouter } from "../../hooks/useRouter";
import api, {
  API_GET_LOGO,
  API_GET_REAL_DOMAIN,
  API_GET_POLL_LIST,
} from "../../utils/api";

import { getProticoClient } from "../../utils/api";
import { postWWWCombine, putPatchWhiteboardUrl } from "../../utils/api/url";
import {
  combineWWWPrefix,
  hashCode,
  parseUrl,
  removeWWWPrefix,
} from "../../utils/format.utils";
import { getSecrets } from "../../config";
import { UrlSettingItem } from "protico-sdk";
import { useViewport } from "../../hooks/useViewport";
import {
  addQueryParamsFromString,
  normalizeUrlForWhitelistedParams,
} from "./util";

interface IRoomContext {
  room: string | undefined;
  roomDomain: string | undefined;
  roomUrl: string | undefined;
  pageUrl: string | undefined;
  isPremium: boolean;
  enableDappAds: boolean;
  sameOriginAds: boolean;
  gameCenterDappEnable: boolean;
  roomError: RoomInitializationError | undefined;
  logoUrl: string | undefined;
  backgroundImageUrl: string | undefined;
  announcementId: string | undefined;
  pollData: any | undefined;
  pollDataIndex: number | undefined;
  setPollDataIndex: (index: number) => void;
  isLoadingImage: boolean | undefined;
  notitficationUnreadCount: number;
  setNotificationUnreadCount: (count: number) => void;
  resetRoom: (root: boolean) => void;
  isOnDefaultRoom: boolean;
}

interface IRoomAndAnnouncementUrl {
  roomUrl: string;
  announcementUrl: string | null;
  is_premium: boolean;
  parsedPageUrl: string;
}

export const roomContext = React.createContext<IRoomContext>({
  room: undefined,
  roomDomain: undefined,
  roomUrl: undefined,
  pageUrl: undefined,
  isPremium: false,
  enableDappAds: false,
  sameOriginAds: false,
  gameCenterDappEnable: false,
  roomError: undefined,
  logoUrl: undefined,
  backgroundImageUrl: undefined,
  announcementId: undefined,
  pollData: undefined,
  pollDataIndex: undefined,
  setPollDataIndex: () => {},
  isLoadingImage: undefined,
  notitficationUnreadCount: 0,
  setNotificationUnreadCount: () => {},
  resetRoom: () => {},
  isOnDefaultRoom: false,
});

const getIterUrls = (url: string) => {
  const parseResult = urlParse(url);
  const paths = parseResult.pathname;
  const pathIter = paths.split("/").filter((el) => el.length > 0);
  const protocol = ["https:", "http:"].includes(parseResult.protocol)
    ? parseResult.protocol
    : "https:";
  var base = `${protocol}//${parseResult.host}`;
  var iterUrls = [base + "/"];
  pathIter.forEach((path) => {
    base += "/" + path;
    iterUrls.push(base);
  });
  return iterUrls.reverse();
};

const getExtensionCompatibleUrl = (url: string) =>
  parseUrl(combineWWWPrefix(url));

const parseUrlToRoomAndAnnouncementUrls = async (
  url: string,
  query: any,
  protico_oq: string = ""
): Promise<IRoomAndAnnouncementUrl> => {
  url = getExtensionCompatibleUrl(url);
  if (
    window.location !== window.parent.location &&
    !url.includes("code-generator") &&
    typeof query.href !== "undefined"
  ) {
    let referrer = getExtensionCompatibleUrl(query.href);
    if (urlParse(referrer).host !== urlParse(url).host)
      throw RoomInitializationError.unauthorizedUrl(
        "The host must be the same when using iframe"
      );
  }

  let urlSettings: UrlSettingItem[];
  let use_separate_url: boolean;
  let is_premium: boolean = false;
  let allow_query_route: boolean = false;
  let query_route_whitelist: string[] = [];

  try {
    const host = urlParse(url).host;
    // Run both API calls in parallel
    const [settingsResponse, domainSettingsResponse] = await Promise.all([
      getProticoClient().settings.get(host),
      getProticoClient().settings.getDomainSettings(host),
    ]);

    urlSettings = settingsResponse;
    use_separate_url = domainSettingsResponse.use_separate_url;
    is_premium = domainSettingsResponse.is_premium;
    allow_query_route = domainSettingsResponse.allow_query_route;
    query_route_whitelist = domainSettingsResponse.query_route_whitelists.map(
      (w) => w.param
    );
  } catch (e) {
    // case1: There is no url settings for this domain
    return { roomUrl: url, announcementUrl: null, is_premium: is_premium, parsedPageUrl: url };
  }

  if (urlSettings.length === 0) {
    // case1: There is no url settings for this domain
    return { roomUrl: url, announcementUrl: null, is_premium: is_premium, parsedPageUrl: url };
  }

  let matchedUrl = null;
  // This url is belongs to a registered url setting
  const iterUrls = getIterUrls(url);
  const urlSettingStrings = urlSettings.map((el) => el.url);
  for (var pattern of iterUrls) {
    if (urlSettingStrings.includes(pattern)) {
      matchedUrl = pattern;
      break;
    }
  }
  if (matchedUrl === null) {
    throw RoomInitializationError.unexpectedUrlSettings(
      `Cannot find correct urlsettings, find urlsettings: ${JSON.stringify(
        urlSettings
      )}, iterUrls: ${JSON.stringify(iterUrls)}`
    );
  }

  if (use_separate_url) {
    if (allow_query_route) {
      const fullUrl = addQueryParamsFromString(url, protico_oq);
      url = normalizeUrlForWhitelistedParams(fullUrl, query_route_whitelist);
      // console.log("normalizeUrlForWhitelistedParams: ", url)
    }
    // case2: There is a url setting for this domain and use_separate_url is true
    return {
      roomUrl: url,
      announcementUrl: matchedUrl,
      is_premium: is_premium,
      parsedPageUrl: url,
    };
  } else {
    // case3: There is a url setting for this domain and use_separate_url is false
    return {
      roomUrl: matchedUrl,
      announcementUrl: matchedUrl,
      is_premium: is_premium,
      parsedPageUrl: url,
    };
  }
};
const isUrl = (url: string) => {
  return url.includes("https://") || url.includes("http://");
};

const isHttpUrl = (url: string) => {
  return url.substring(0, 7) === "http://";
};

export const RoomProvider = ({ children }: { children: React.ReactNode }) => {
  const [room, setRoom] = React.useState<string>();
  const [roomDomain, setRoomDomain] = React.useState<string>();
  const [isPremium, setIsPremium] = React.useState<boolean>(false);
  const [roomUrl, setRoomUrl] = React.useState<string>();
  const [pageUrl, setPageUrl] = React.useState<string>();
  const [enableDappAds, setEnableDappAds] = React.useState<boolean>(false);
  const [sameOriginAds, setSameOriginAds] = React.useState<boolean>(false);
  const [gameCenterDappEnable, setGameCenterDappEnable] =
    React.useState<boolean>(false);
  const [logoUrl, setLogoUrl] = React.useState<string>();
  const [backgroundImageUrl, setBackgroundImageUrl] = React.useState<string>();
  const [isLoadingImage, seIsLoadingImage] = React.useState<boolean>(true);
  const [announcementId, setAnnouncementId] = React.useState<string | null>();
  const [pollData, setPollData] = React.useState<any | null>();
  const [pollDataIndex, setPollDataIndex] = React.useState<number>(0);
  const [notitficationUnreadCount, setNotificationUnreadCount] =
    React.useState<number>(0);
  const [isOnDefaultRoom, setIsOnDefaultRoom] = React.useState<boolean>(false);
  const [previousUrl, setPreviousUrl] = React.useState<string | null>(null);
  const [previousOq, setPreviousOq] = React.useState<string | null>(null);
  const isOnDefaultRoomRef = React.useRef(isOnDefaultRoom);

  const { query } = useRouter();
  const [roomError, setRoomError] = React.useState<RoomInitializationError>();
  const {
    LOGO_BASE_URL,
    BACKGROUND_IMAGE_BASE_URL,
    BACKGROUND_IMAGE_GLOBAL_URL,
    DASHBOARD_DOMAIN,
    NODE_ENV,
  } = getSecrets();
  const { isAppOpened } = useViewport();

  React.useEffect(() => {
    isOnDefaultRoomRef.current = isOnDefaultRoom;
  }, [isOnDefaultRoom]);

  const resetRoom = async (root: boolean) => {
    try {
      // @ts-ignore
      if (typeof query.roomUrl !== "undefined") {
        // @ts-ignore
        let url = query.roomUrl as string;
        let protico_oq = "";

        if (root) {
          url = urlParse(url).protocol + "//" + urlParse(url).host;
        } else if (previousUrl) {
          url = previousUrl;
          protico_oq = previousOq ?? "";
        } else {
          // @ts-ignore
          protico_oq = query.protico_oq ?? "";
        }
        // @ts-ignore
        const enable_dapp_ads = query.enable_dapp_ads as string;
        // @ts-ignore
        const same_origin_ads = query.same_origin_ads as string;
        // @ts-ignore
        const game_center_dapp_enable = query.game_center_dapp_enable as string;
        // check if the url is start with http://
        if (isHttpUrl(url) && NODE_ENV === "production") {
          throw RoomInitializationError.unexpectedUrlSettings(
            "The url is not start with https://"
          );
        }
        const {
          roomUrl: prunedUrl,
          parsedPageUrl,
          announcementUrl,
          is_premium,
        } = await parseUrlToRoomAndAnnouncementUrls(url, query, protico_oq);
        setIsPremium(is_premium);
        api
          .get(API_GET_POLL_LIST, {
            params: { whiteboard: prunedUrl },
          })
          .then((response) => {
            setPollData(response.data);
          })
          .catch((error) => {
            console.error("Error fetching poll data:", error);
          });

        if (url.includes(DASHBOARD_DOMAIN + "/code-generator")) {
          const real_domain_response = await api.get(API_GET_REAL_DOMAIN, {
            params: { domain: url.split("/").pop() },
          });
          setAnnouncementId(hashCode(real_domain_response.data.url));
        } else {
          setAnnouncementId(
            !!announcementUrl ? hashCode(announcementUrl) : null
          );
        }
        const hashedRoom = hashCode(prunedUrl);
        setRoomUrl(prunedUrl);
        setPageUrl(parsedPageUrl);
        setEnableDappAds(
          enable_dapp_ads === "true" || enable_dapp_ads === "True"
        );
        setSameOriginAds(
          same_origin_ads === "true" || same_origin_ads === "True"
        );
        setGameCenterDappEnable(
          game_center_dapp_enable === "true" ||
            game_center_dapp_enable === "True"
        );
        postWWWCombine(prunedUrl);
        putPatchWhiteboardUrl(prunedUrl, hashedRoom);
        setRoomDomain(urlParse(prunedUrl).host);
        setRoom(hashedRoom);
      } else {
        // dev mode
        // @ts-ignore
        const enable_dapp_ads = query.enable_dapp_ads as string;
        // @ts-ignore
        const same_origin_ads = query.same_origin_ads as string;
        // @ts-ignore
        const game_center_dapp_enable = query.game_center_dapp_enable as string;
        const roomUrl = parseUrl(document.location.href);
        console.log("using dev url: ", roomUrl);
        const hashedRoom = hashCode(roomUrl);
        putPatchWhiteboardUrl(roomUrl, hashedRoom);
        setRoomUrl(roomUrl);
        setPageUrl(roomUrl);
        setEnableDappAds(enable_dapp_ads === "true");
        setSameOriginAds(same_origin_ads === "true");
        setGameCenterDappEnable(game_center_dapp_enable === "true");
        setRoomDomain(urlParse(roomUrl).host);
        setAnnouncementId(hashedRoom);
        setRoom(hashedRoom);
      }
      setIsOnDefaultRoom(root);
    } catch (error) {
      console.error(error);
      setRoomError(
        error instanceof RoomInitializationError
          ? error
          : RoomInitializationError.fromCode(5000, error.message)
      );
    }
  };

  const resetRoomToUrl = async (newUrl: string, protico_oq: string) => {
    try {
      // @ts-ignore
      if (typeof query.roomUrl !== "undefined") {
        // @ts-ignore
        const url = newUrl as string;
        // @ts-ignore
        const enable_dapp_ads = query.enable_dapp_ads as string;
        // @ts-ignore
        const same_origin_ads = query.same_origin_ads as string;
        // @ts-ignore
        const game_center_dapp_enable = query.game_center_dapp_enable as string;
        // check if the url is start with http://
        if (isHttpUrl(url) && NODE_ENV === "production") {
          throw RoomInitializationError.unexpectedUrlSettings(
            "The url is not start with https://"
          );
        }
        const {
          roomUrl: prunedUrl,
          parsedPageUrl,
          announcementUrl,
          is_premium,
        } = await parseUrlToRoomAndAnnouncementUrls(url, query, protico_oq);
        setIsPremium(is_premium);
        api
          .get(API_GET_POLL_LIST, {
            params: { whiteboard: prunedUrl },
          })
          .then((response) => {
            setPollData(response.data);
          })
          .catch((error) => {
            console.error("Error fetching poll data:", error);
          });

        if (url.includes(DASHBOARD_DOMAIN + "/code-generator")) {
          const real_domain_response = await api.get(API_GET_REAL_DOMAIN, {
            params: { domain: url.split("/").pop() },
          });
          setAnnouncementId(hashCode(real_domain_response.data.url));
        } else {
          setAnnouncementId(
            !!announcementUrl ? hashCode(announcementUrl) : null
          );
        }
        const hashedRoom = hashCode(prunedUrl);
        setRoomUrl(prunedUrl);
        setPageUrl(parsedPageUrl);
        setEnableDappAds(
          enable_dapp_ads === "true" || enable_dapp_ads === "True"
        );
        setSameOriginAds(
          same_origin_ads === "true" || same_origin_ads === "True"
        );
        setGameCenterDappEnable(
          game_center_dapp_enable === "true" ||
            game_center_dapp_enable === "True"
        );
        postWWWCombine(prunedUrl);
        putPatchWhiteboardUrl(prunedUrl, hashedRoom);
        setRoomDomain(urlParse(prunedUrl).host);
        setRoom(hashedRoom);
      } else {
        // dev mode
        // @ts-ignore
        const enable_dapp_ads = query.enable_dapp_ads as string;
        // @ts-ignore
        const same_origin_ads = query.same_origin_ads as string;
        // @ts-ignore
        const game_center_dapp_enable = query.game_center_dapp_enable as string;
        const roomUrl = parseUrl(document.location.href);
        console.log("using dev url: ", roomUrl);
        const hashedRoom = hashCode(roomUrl);
        putPatchWhiteboardUrl(roomUrl, hashedRoom);
        setRoomUrl(roomUrl);
        setPageUrl(roomUrl);
        setEnableDappAds(enable_dapp_ads === "true");
        setSameOriginAds(same_origin_ads === "true");
        setGameCenterDappEnable(game_center_dapp_enable === "true");
        setRoomDomain(urlParse(roomUrl).host);
        setAnnouncementId(hashedRoom);
        setRoom(hashedRoom);
      }
    } catch (error) {
      console.error(error);
      setRoomError(
        error instanceof RoomInitializationError
          ? error
          : RoomInitializationError.fromCode(5000, error.message)
      );
    }
  };

  const handleMessage = (event: any) => {
    // Ensure the message is from the expected source if needed
    // if (event.origin !== 'http://your-origin') return;
    if (
      event.data &&
      typeof event.data.urlChange === "string" &&
      typeof event.data.protico_oq === "string"
    ) {
      // Handle the message here, for example:
      if (event.data.urlChange && event.data.protico_oq) {
        if (!isOnDefaultRoomRef.current) {
          resetRoomToUrl(event.data.urlChange, event.data.protico_oq);
        } else {
          setPreviousUrl(event.data.urlChange);
          setPreviousOq(event.data.protico_oq);
        }
      } else {
      }
    }
  };

  React.useEffect(() => {
    const init = async () => {
      try {
        // @ts-ignore
        if (typeof query.roomUrl !== "undefined") {
          // @ts-ignore
          const url = query.roomUrl as string;
          // @ts-ignore
          const enable_dapp_ads = query.enable_dapp_ads as string;
          // @ts-ignore
          const same_origin_ads = query.same_origin_ads as string;
          const game_center_dapp_enable =
          // @ts-ignore
            query.game_center_dapp_enable as string;
          // @ts-ignore
          const protico_oq = (query.protico_oq ?? "") as string;
          // check if the url is start with http://
          if (isHttpUrl(url) && NODE_ENV === "production") {
            throw RoomInitializationError.unexpectedUrlSettings(
              "The url is not start with https://"
            );
          }
          const {
            roomUrl: prunedUrl,
            parsedPageUrl,
            announcementUrl,
            is_premium,
          } = await parseUrlToRoomAndAnnouncementUrls(url, query, protico_oq);
          setIsPremium(is_premium);
          api
            .get(API_GET_POLL_LIST, {
              params: { whiteboard: prunedUrl },
            })
            .then((response) => {
              setPollData(response.data);
            })
            .catch((error) => {
              console.error("Error fetching poll data:", error);
            });

          if (url.includes(DASHBOARD_DOMAIN + "/code-generator")) {
            const real_domain_response = await api.get(API_GET_REAL_DOMAIN, {
              params: { domain: url.split("/").pop() },
            });
            setAnnouncementId(hashCode(real_domain_response.data.url));
          } else {
            setAnnouncementId(
              !!announcementUrl ? hashCode(announcementUrl) : null
            );
          }
          const hashedRoom = hashCode(prunedUrl);
          setRoomUrl(prunedUrl);
          setPageUrl(parsedPageUrl);
          setEnableDappAds(
            enable_dapp_ads === "true" || enable_dapp_ads === "True"
          );
          setSameOriginAds(
            same_origin_ads === "true" || same_origin_ads === "True"
          );
          setGameCenterDappEnable(
            game_center_dapp_enable === "true" ||
              game_center_dapp_enable === "True"
          );
          postWWWCombine(prunedUrl);
          putPatchWhiteboardUrl(prunedUrl, hashedRoom);
          setRoomDomain(urlParse(prunedUrl).host);
          setRoom(hashedRoom);
        } else {
          // dev mode
          // @ts-ignore
          const enable_dapp_ads = query.enable_dapp_ads as string;
          // @ts-ignore
          const same_origin_ads = query.same_origin_ads as string;
          const game_center_dapp_enable =
          // @ts-ignore
            query.game_center_dapp_enable as string;
          const roomUrl = parseUrl(document.location.href);
          console.log("using dev url: ", roomUrl);
          const hashedRoom = hashCode(roomUrl);
          putPatchWhiteboardUrl(roomUrl, hashedRoom);
          setRoomUrl(roomUrl);
          setPageUrl(roomUrl);
          setEnableDappAds(enable_dapp_ads === "true");
          setSameOriginAds(same_origin_ads === "true");
          setGameCenterDappEnable(game_center_dapp_enable === "true");
          setRoomDomain(urlParse(roomUrl).host);
          setAnnouncementId(hashedRoom);
          setRoom(hashedRoom);
        }
      } catch (error) {
        console.error(error);
        setRoomError(
          error instanceof RoomInitializationError
            ? error
            : RoomInitializationError.fromCode(5000, error.message)
        );
      }
      window.addEventListener("message", handleMessage);
    };
    if (isAppOpened) init();
  }, [isAppOpened]);

  React.useEffect(() => {
    const fetchData = async (logoDomain: string) => {
      let response: any;
      try {
        response = await api.get(API_GET_LOGO + logoDomain);
        const prefix_www_url = removeWWWPrefix(logoDomain);
        if (response.data.logo && response.data.logo.file_name) {
          setLogoUrl(
            LOGO_BASE_URL + prefix_www_url + "/" + response.data.logo.file_name
          );
        }
        if (response.data.background && response.data.background.file_name) {
          if (response.data.use_global) {
            setBackgroundImageUrl(
              BACKGROUND_IMAGE_GLOBAL_URL + response.data.background.file_name
            );
          } else {
            setBackgroundImageUrl(
              BACKGROUND_IMAGE_BASE_URL +
                prefix_www_url +
                "/" +
                response.data.background.file_name
            );
          }
        }
        //setTimeout(() => {
        //  seIsLoadingImage(false); // Set 0.1 sec to let the custom logo be displayed
        //}, 200);
        seIsLoadingImage(false);
      } catch (error) {
        console.error(`Failed to fetch logo for ${logoDomain}`);
        seIsLoadingImage(false);
      }
    };

    const getLogoDomain = async (url: string) => {
      if (url.includes(DASHBOARD_DOMAIN + "/code-generator")) {
        const real_domain_response = await api.get(API_GET_REAL_DOMAIN, {
          params: { domain: url.split("/").pop() },
        });
        return urlParse(real_domain_response.data.url).host;
      } else {
        return urlParse(url).host;
      }
    };

    // @ts-ignore
    if (typeof query.roomUrl !== "undefined") {
      // @ts-ignore
      const url = query.roomUrl as string;
      if (!isUrl(url)) return;
      getLogoDomain(url)
        .then((logoDomain) => fetchData(logoDomain))
        .catch(console.error);
    } else {
      seIsLoadingImage(false);
    }
  }, []);

  return (
    <roomContext.Provider
      value={{
        room,
        roomDomain,
        roomUrl,
        pageUrl,
        isPremium,
        enableDappAds,
        sameOriginAds,
        gameCenterDappEnable,
        roomError,
        logoUrl,
        backgroundImageUrl,
        announcementId,
        pollData,
        pollDataIndex,
        setPollDataIndex,
        isLoadingImage,
        notitficationUnreadCount,
        setNotificationUnreadCount,
        resetRoom,
        isOnDefaultRoom,
      }}
    >
      {children}
    </roomContext.Provider>
  );
};
